import type * as SentryTypes from '@sentry/react';
import { logger } from '@/shared/utils/logger';

// Declared in vite.config.mts define block
declare const __APP_VERSION__: string;
declare const __GIT_SHA__: string;

/**
 * Sentry client initialization (lazy-loaded)
 * The @sentry/react SDK is dynamically imported to keep it out of the initial bundle.
 * It loads on first call to initializeSentry().
 */

interface SentryConfig {
  enabled?: boolean;
  environment?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  beforeSend?: (event: SentryTypes.ErrorEvent, hint: SentryTypes.EventHint) => SentryTypes.ErrorEvent | PromiseLike<SentryTypes.ErrorEvent | null> | null;
}

// Track initialization state to prevent duplicate initialization
let isInitialized = false;

// Lazily loaded Sentry module
let SentryModule: typeof SentryTypes | null = null;

/**
 * Get the loaded Sentry module instance.
 * Returns null if Sentry has not been initialized yet.
 */
export const getSentry = (): typeof SentryTypes | null => SentryModule;

/**
 * Initialize Sentry with the provided configuration.
 * Dynamically imports @sentry/react on first call.
 * Returns true if initialization was successful, false otherwise.
 */
export const initializeSentry = async (config?: SentryConfig): Promise<boolean> => {
  // Prevent duplicate initialization
  if (isInitialized) {
    return true;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    return false;
  }

  const environment = config?.environment || import.meta.env.VITE_APP_ENV || 'production';
  const isProduction = environment === 'production';

  try {
    SentryModule = await import('@sentry/react');

    SentryModule.init({
      dsn,
      environment,
      release: typeof __APP_VERSION__ !== 'undefined'
        ? `donvio-frontend@${__APP_VERSION__}+${typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : 'dev'}`
        : undefined,
      enabled: config?.enabled ?? import.meta.env.PROD,
      integrations: [
        SentryModule.browserTracingIntegration(),
        SentryModule.replayIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: config?.tracesSampleRate ?? (isProduction ? 0.1 : 1.0),
      // Session Replay
      replaysSessionSampleRate: config?.replaysSessionSampleRate ?? 0.1,
      replaysOnErrorSampleRate: config?.replaysOnErrorSampleRate ?? 1.0,
      // Custom beforeSend hook
      beforeSend: config?.beforeSend,
    });

    isInitialized = true;
    return true;
  } catch (error) {
    logger.error('Failed to initialize Sentry', error);
    return false;
  }
};

/**
 * Close Sentry client and stop sending events
 * Used when user revokes analytics consent
 */
export const closeSentry = async (): Promise<void> => {
  if (SentryModule) {
    await SentryModule.close();
  }
  // Reset initialization flag to allow re-initialization
  isInitialized = false;
};
